import React, { useRef, useState } from 'react'
import cx from 'classnames'
import { AvatarRound, List, l, PrimaryNavigation } from '@manychat/manyui'

import { useAppSelector } from 'reduxTyped'
import { UpgradeSource } from 'common/billing'
import GoProBtn from 'common/billing/components/GoProBtn'
import { TRIAL_PERIOD_IN_DAYS } from 'common/billing/constants'
import { useMigrationTrialLabel } from 'common/billing/hooks/useMigrationTrialLabel'
import ContactsLimit from 'common/core/components/ContactsLimit'
import HelpPopover from 'common/core/components/HelpPopover/HelpPopover'
import MyProfilePopover from 'common/core/components/MyProfilePopover/MyProfilePopover'
import { getProfileDashboardStatus, getUser } from 'common/core/selectors/appSelectors'
import { getCurrentAccountIsDummyMode } from 'common/core/selectors/currentAccountSelectors'
import { FusionSidebarLink } from 'common/fusion/components/FusionSidebarLink'
import { IfHasPermission, PermissionTargets } from 'common/userRoles'
import { getProfileImageStubURL } from 'utils/assets'
import { analyticsService } from 'utils/services/analytics'
import { AccountFlag } from 'utils/services/featureFlagsService/constants'
import { useAccountReleaseToggle } from 'utils/services/featureFlagsService/releaseTogglesHooks'
import { getBrandsSiteUrl } from 'utils/url'

import { useBottomContentParams } from './hooks/useBottomContentParams'
import { MyProfilePopoverAnalytics } from './MyProfileLink/MyProfileLinkPopover/lib/analytics'
import { MyProfileLinkPopover } from './MyProfileLink/MyProfileLinkPopover/MyProfileLinkPopover'

export const BottomContent = () => {
  const [isMyProfileOpen, setIsMyProfileOpen] = useState(false)
  const [isHelpOpen, setIsHelpOpen] = useState(false)
  const myProfileButtonRef = useRef<HTMLButtonElement>(null)
  const helpButtonRef = useRef<HTMLButtonElement>(null)
  const isDummyMode = useAppSelector(getCurrentAccountIsDummyMode)

  const { pageId, isPRO, isContactsLimitBanner, isRetryPayment, hasTrial, isSidebarHidden } =
    useBottomContentParams()

  const showProBlock = !isDummyMode && !isPRO

  const profile = useAppSelector(getUser)
  const hasProfileDashboard = useAppSelector(getProfileDashboardStatus)
  const trialLabel = useMigrationTrialLabel()
  const isBrandsEntrypointEnabled = useAccountReleaseToggle(AccountFlag.ENABLE_BRANDS_ENTRYPOINT)

  const handleHelpClick = () => {
    if (!isHelpOpen) {
      analyticsService.sendEvent('HELP.CLICK')
    }
    setIsHelpOpen((prev) => !prev)
  }

  const handleMyProfileClick = () => {
    if (!isMyProfileOpen) {
      MyProfilePopoverAnalytics.logOpenPopover()
    }
    setIsMyProfileOpen((prev) => !prev)
  }

  const handleMyProfileClose = () => {
    MyProfilePopoverAnalytics.logClosePopover()
    setIsMyProfileOpen(false)
  }

  const getSidebarUpgradeLabel = (): string | undefined => {
    if (isSidebarHidden) return 'PRO'
    if (hasTrial) {
      return l.getString(
        trialLabel ??
          l.translate('Try {daysCount} days for free', { daysCount: TRIAL_PERIOD_IN_DAYS }),
      )
    }
    return undefined
  }

  return (
    <>
      <List className="p-a-xs d-flex flex-col">
        <FusionSidebarLink collapsed={isSidebarHidden} />
        <PrimaryNavigation.Item
          as="button"
          icon={
            <AvatarRound
              size="xsmall"
              src={profile?.avatar_small}
              placeholderSrc={getProfileImageStubURL()}
              alt="My profile avatar"
            />
          }
          ariaLabel={l.translate('My Profile')}
          tooltip={l.translate('My Profile')}
          collapsed={isSidebarHidden}
          ref={myProfileButtonRef}
          onClick={handleMyProfileClick}
          className="m-l-xxs"
          data-test-id="profile-popover"
        >
          {l.translate('My Profile')}
        </PrimaryNavigation.Item>
        {isBrandsEntrypointEnabled && (
          <PrimaryNavigation.Item
            as="a"
            href={getBrandsSiteUrl()}
            icon="Collab"
            ariaLabel={l.translate('Manychat for Brands')}
            tooltip={l.translate('Manychat for Brands')}
            collapsed={isSidebarHidden}
            className="m-l-xxs"
            data-test-id="brands-nav-item"
          >
            {l.translate('Manychat for Brands')}
          </PrimaryNavigation.Item>
        )}
        <PrimaryNavigation.Item
          icon="Question"
          ariaLabel={l.translate('Help')}
          tooltip={l.translate('Help')}
          collapsed={isSidebarHidden}
          as="button"
          onClick={handleHelpClick}
          className="m-l-xxs"
          ref={helpButtonRef}
          data-test-id="help-popover"
        >
          {l.translate('Help')}
        </PrimaryNavigation.Item>
        {hasProfileDashboard ? (
          <MyProfileLinkPopover
            isOpen={isMyProfileOpen}
            anchor={myProfileButtonRef.current || null}
            onClose={handleMyProfileClose}
          />
        ) : (
          <MyProfilePopover
            anchor={myProfileButtonRef.current || null}
            open={isMyProfileOpen}
            onRequestClose={handleMyProfileClose}
          />
        )}
        <HelpPopover
          open={isHelpOpen}
          anchor={helpButtonRef.current || undefined}
          pageId={pageId}
          onRequestClose={() => setIsHelpOpen(false)}
          useLayerForClickAway
        />
      </List>
      {showProBlock && (
        <div className={cx({ 'b-t': isContactsLimitBanner })}>
          {isContactsLimitBanner && (
            <ContactsLimit
              isSidebarHidden={isSidebarHidden}
              fbPagesOpen={!isSidebarHidden}
              isRightTimeAnimate={false}
            />
          )}
          <IfHasPermission target={PermissionTargets.SETTINGS_BILLING}>
            {!isRetryPayment ? (
              <div className={cx('m-b-sm', isSidebarHidden ? 'm-x-xxs' : 'm-x')}>
                <GoProBtn
                  request={{
                    source: UpgradeSource.SIDEBAR,
                  }}
                  label={getSidebarUpgradeLabel()}
                  forceLabel
                  size="small"
                  className={cx(isSidebarHidden && 'p-x-xxs')}
                  variant={hasTrial ? 'success' : 'primary'}
                  fullWidth
                  isSidebarHidden={isSidebarHidden}
                />
              </div>
            ) : null}
          </IfHasPermission>
        </div>
      )}
    </>
  )
}
